import { twMerge } from "tailwind-merge";

type SkeletonSize = "xs" | "sm" | "md" | "lg" | "xl";
type SkeletonWidth =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "full"
  | "1/2"
  | "1/3"
  | "2/3"
  | "3/4";

interface SkeletonProps extends React.ComponentProps<"div"> {
  readonly isLoaded?: boolean;
  readonly stagger?: number;
  readonly staggerDelay?: number;
}

const BASE_STAGGER_DELAY = 75;

function Skeleton({
  className,
  isLoaded,
  stagger,
  staggerDelay = BASE_STAGGER_DELAY,
  style,
  ...props
}: Readonly<SkeletonProps>) {
  if (isLoaded) return props.children;

  const animationDelay =
    stagger !== undefined ? stagger * staggerDelay : undefined;

  return (
    <div
      data-slot="skeleton"
      className={twMerge("animate-pulse rounded-md bg-background", className)}
      style={{
        ...style,
        ...(animationDelay !== undefined && {
          animationDelay: `${animationDelay}ms`,
        }),
      }}
      {...props}
    />
  );
}

const SIZE_HEIGHT: Record<SkeletonSize, string> = {
  xs: "h-3",
  sm: "h-4",
  md: "h-5",
  lg: "h-6",
  xl: "h-8",
};

const WIDTH_CLASS: Record<SkeletonWidth, string> = {
  xs: "w-16",
  sm: "w-24",
  md: "w-32",
  lg: "w-48",
  full: "w-full",
  "1/2": "w-1/2",
  "1/3": "w-1/3",
  "2/3": "w-2/3",
  "3/4": "w-3/4",
};

interface SkeletonTextProps extends Omit<SkeletonProps, "children"> {
  readonly size?: SkeletonSize;
  readonly width?: SkeletonWidth;
}

function SkeletonText({
  size = "sm",
  width = "full",
  className,
  ...props
}: Readonly<SkeletonTextProps>) {
  return (
    <Skeleton
      className={twMerge(
        SIZE_HEIGHT[size],
        WIDTH_CLASS[width],
        "rounded-sm",
        className,
      )}
      {...props}
    />
  );
}

interface SkeletonParagraphProps extends Omit<SkeletonProps, "children"> {
  readonly lines?: number;
  readonly size?: SkeletonSize;
  readonly gap?: "sm" | "md" | "lg";
}

const GAP_CLASS = {
  sm: "space-y-2",
  md: "space-y-3",
  lg: "space-y-4",
};

function SkeletonParagraph({
  lines = 3,
  size = "sm",
  gap = "md",
  className,
  stagger,
  staggerDelay,
  ...props
}: Readonly<SkeletonParagraphProps>) {
  const widths: SkeletonWidth[] = ["full", "full", "3/4", "2/3", "1/2"];

  return (
    <div className={twMerge(GAP_CLASS[gap], className)} {...props}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonText
          // oxlint-disable-next-line react/no-array-index-key
          key={i}
          size={size}
          width={widths[i] ?? "full"}
          stagger={stagger !== undefined ? stagger + i : undefined}
          staggerDelay={staggerDelay}
        />
      ))}
    </div>
  );
}

interface SkeletonAvatarProps extends Omit<SkeletonProps, "children"> {
  readonly size?: SkeletonSize | number;
}

const AVATAR_SIZE: Record<SkeletonSize, string> = {
  xs: "size-6",
  sm: "size-8",
  md: "size-10",
  lg: "size-12",
  xl: "size-16",
};

function SkeletonAvatar({
  size = "md",
  className,
  style,
  ...props
}: Readonly<SkeletonAvatarProps>) {
  const isNumeric = typeof size === "number";

  return (
    <Skeleton
      className={twMerge(
        "shrink-0 rounded-full",
        !isNumeric && AVATAR_SIZE[size],
        className,
      )}
      style={isNumeric ? { ...style, width: size, height: size } : style}
      {...props}
    />
  );
}

interface SkeletonButtonProps extends Omit<SkeletonProps, "children"> {
  readonly size?: SkeletonSize;
  readonly width?: SkeletonWidth;
}

const BUTTON_HEIGHT: Record<SkeletonSize, string> = {
  xs: "h-7",
  sm: "h-8",
  md: "h-10",
  lg: "h-11",
  xl: "h-12",
};

function SkeletonButton({
  size = "md",
  width = "sm",
  className,
  ...props
}: Readonly<SkeletonButtonProps>) {
  return (
    <Skeleton
      className={twMerge(
        BUTTON_HEIGHT[size],
        WIDTH_CLASS[width],
        "rounded-lg",
        className,
      )}
      {...props}
    />
  );
}

interface SkeletonInputProps extends Omit<SkeletonProps, "children"> {
  readonly size?: SkeletonSize;
  readonly width?: SkeletonWidth;
  readonly withLabel?: boolean;
}

function SkeletonInput({
  size = "md",
  width = "full",
  withLabel = false,
  className,
  stagger,
  staggerDelay,
  ...props
}: Readonly<SkeletonInputProps>) {
  const input = (
    <Skeleton
      className={twMerge(
        BUTTON_HEIGHT[size],
        WIDTH_CLASS[width],
        "rounded-lg",
        className,
      )}
      stagger={withLabel && stagger !== undefined ? stagger + 1 : stagger}
      staggerDelay={staggerDelay}
      {...props}
    />
  );

  if (!withLabel) return input;

  return (
    <div className="space-y-2">
      <SkeletonText
        size="xs"
        width="sm"
        stagger={stagger}
        staggerDelay={staggerDelay}
      />
      {input}
    </div>
  );
}

interface SkeletonCardProps extends Omit<SkeletonProps, "children"> {
  readonly height?: string;
}

function SkeletonCard({
  height = "h-32",
  className,
  ...props
}: Readonly<SkeletonCardProps>) {
  return (
    <Skeleton
      className={twMerge(
        height,
        "w-full rounded-lg border border-border",
        className,
      )}
      {...props}
    />
  );
}

interface SkeletonIconProps extends Omit<SkeletonProps, "children"> {
  readonly size?: SkeletonSize | number;
}

const ICON_SIZE: Record<SkeletonSize, string> = {
  xs: "size-4",
  sm: "size-5",
  md: "size-6",
  lg: "size-8",
  xl: "size-10",
};

function SkeletonIcon({
  size = "md",
  className,
  style,
  ...props
}: Readonly<SkeletonIconProps>) {
  const isNumeric = typeof size === "number";

  return (
    <Skeleton
      className={twMerge(
        "shrink-0 rounded-sm",
        !isNumeric && ICON_SIZE[size],
        className,
      )}
      style={isNumeric ? { ...style, width: size, height: size } : style}
      {...props}
    />
  );
}

interface SkeletonListProps extends Omit<SkeletonProps, "children"> {
  readonly count?: number;
  readonly gap?: "sm" | "md" | "lg";
  readonly children: (index: number) => React.ReactNode;
}

function SkeletonList({
  count = 3,
  gap = "md",
  children,
  className,
  ...props
}: Readonly<SkeletonListProps>) {
  return (
    <div className={twMerge(GAP_CLASS[gap], className)} {...props}>
      {Array.from({ length: count }).map((_, i) => children(i))}
    </div>
  );
}

Skeleton.Text = SkeletonText;
Skeleton.Paragraph = SkeletonParagraph;
Skeleton.Avatar = SkeletonAvatar;
Skeleton.Button = SkeletonButton;
Skeleton.Input = SkeletonInput;
Skeleton.Card = SkeletonCard;
Skeleton.Icon = SkeletonIcon;
Skeleton.List = SkeletonList;

export { Skeleton };
export type {
  SkeletonProps,
  SkeletonTextProps,
  SkeletonParagraphProps,
  SkeletonAvatarProps,
  SkeletonButtonProps,
  SkeletonInputProps,
  SkeletonCardProps,
  SkeletonIconProps,
  SkeletonListProps,
  SkeletonSize,
  SkeletonWidth,
};
